import Box from '@mui/material/Box';
import Stack from '@mui/material/Stack';
import { useTheme } from '@mui/material/styles';
import { EntityRefLink } from '@backstage/plugin-catalog-react';

import {
  getSurfaceBorder,
  getSurfaceColor,
  getThemeTokens,
} from '../../../theme/tokens';
import type { EntityPageRelationItem } from './entityPageModel';

const relationLabels: Record<string, string> = {
  apiConsumedBy: 'used by',
  apiProvidedBy: 'provided by',
  consumesApi: 'consumes',
  dependencyOf: 'required by',
  dependsOn: 'depends on',
  hasPart: 'contains',
  partOf: 'belongs to',
  providesApi: 'provides',
};

export type EntityDependencySummaryItem = Pick<
  EntityPageRelationItem,
  'entityRef' | 'title'
> & {
  relations: string[];
};

export const summarizeDependencyItems = (
  items: EntityPageRelationItem[],
): EntityDependencySummaryItem[] => {
  const summaries = new Map<string, EntityDependencySummaryItem>();

  items.forEach(item => {
    const existing = summaries.get(item.entityRef);

    if (existing) {
      if (!existing.relations.includes(item.relation)) {
        existing.relations.push(item.relation);
      }
      return;
    }

    summaries.set(item.entityRef, {
      entityRef: item.entityRef,
      relations: [item.relation],
      title: item.title,
    });
  });

  return [...summaries.values()];
};

const DependencyRelationPill = ({ relation }: { relation: string }) => {
  const theme = useTheme();
  const tokens = getThemeTokens(theme);

  return (
    <Box
      component="span"
      sx={{
        alignItems: 'center',
        backgroundColor: getSurfaceColor(theme, 'subtle'),
        border: `1px solid ${getSurfaceBorder(theme)}`,
        borderRadius: 999,
        color: tokens.textMuted,
        display: 'inline-flex',
        fontSize: 11,
        fontWeight: 700,
        lineHeight: 1,
        px: 1,
        py: 0.55,
        textTransform: 'lowercase',
        whiteSpace: 'nowrap',
      }}
    >
      {relationLabels[relation] ?? relation}
    </Box>
  );
};

const DependencyRow = ({ item }: { item: EntityDependencySummaryItem }) => {
  return (
    <Box
      sx={{
        alignItems: { sm: 'center', xs: 'flex-start' },
        display: 'grid',
        gap: 1.25,
        gridTemplateColumns: {
          sm: 'minmax(0, 1fr) auto',
          xs: 'minmax(0, 1fr)',
        },
        minWidth: 0,
      }}
    >
      <EntityRefLink entityRef={item.entityRef} title={item.title} />
      <Box
        sx={{
          display: 'flex',
          flexWrap: 'wrap',
          gap: 0.75,
          justifyContent: { sm: 'flex-end', xs: 'flex-start' },
          minWidth: 0,
        }}
      >
        {item.relations.map(relation => (
          <DependencyRelationPill
            key={`${item.entityRef}:${relation}`}
            relation={relation}
          />
        ))}
      </Box>
    </Box>
  );
};

export const EntityDependencyList = ({
  items,
}: {
  items: EntityDependencySummaryItem[];
}) => (
  <Stack spacing={1.5}>
    {items.map(item => (
      <DependencyRow key={item.entityRef} item={item} />
    ))}
  </Stack>
);
